import { Code, ConnectError } from "@connectrpc/connect";
import { HttpError } from "../api/error/HttpError";
import { NotFoundError } from "../api/error/NotFoundError";
import { ServerError } from "../api/error/ServerError";
import { UnauthorizedError } from "../api/error/UnauthorizedError";
import { ValidationError } from "../api/error/ValidationError";

/** Traduce un error de una llamada gRPC-Web a la jerarquía de errores tipados
 *  que usa el resto de la app (toasts, `errorService`), para que no importe
 *  de qué rpc vino el error. Mapeo simétrico al que hace el servidor en
 *  `Api/Grpc/*GrpcService.cs` (`ToRpcException`).
 *
 *  Usa `error.rawMessage`, no `error.message`: Connect antepone el código al
 *  mensaje en `.message` (ej. `"[invalid_argument] Usuario o contraseña
 *  incorrectos"`) para que se vea en herramientas de desarrollo — `rawMessage`
 *  es el texto tal cual lo mandó el servidor. */
export function mapConnectError(error: unknown): Error {
  if (!(error instanceof ConnectError)) {
    return new Error("Error de red. Verifica tu conexión a internet.");
  }

  switch (error.code) {
    case Code.InvalidArgument:
      return new ValidationError(error.rawMessage);
    case Code.Unauthenticated:
      // AutenticacionAppInterceptor (Server) responde en formato gRPC-Web con el
      // texto real ("Debe volver a iniciar sesión"). Si el 401 viniera de otro
      // lado sin ese formato (proxy), connect-web sintetiza "HTTP 401": en ese
      // caso, o sin texto, se usa el mismo mensaje por defecto.
      return new UnauthorizedError(
        error.rawMessage && !/^HTTP \d+/.test(error.rawMessage) ? error.rawMessage : "Debe volver a iniciar sesión",
      );
    case Code.NotFound:
      return new NotFoundError(error.rawMessage);
    case Code.Internal:
      return new ServerError(error.rawMessage);
    case Code.Unknown:
      // El servidor nunca devuelve este código a propósito (ver ToRpcException):
      // acá cae una falla de red/transporte.
      return new Error("Error de red. Verifica tu conexión a internet.");
    case Code.PermissionDenied:
      // AutenticacionAppInterceptor (Server): el token es válido pero de otra app
      // (ej. cuenta de Cliente llamando a un servicio de Plataforma). No cierra la
      // sesión: se muestra el mensaje del servidor como cualquier error (403).
      return new HttpError(error.rawMessage, 403);
    default:
      return new HttpError(error.rawMessage, 500);
  }
}
